import { createContext, useContext, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  CircleUser,
  ClipboardList,
  FileText,
  Home,
  LayoutGrid,
  LogOut,
  Inbox,
  Package,
  Plus,
  Settings,
  ShieldCheck,
  ShoppingCart,
  UtensilsCrossed,
  Wallet,
  WifiOff,
  Wrench,
} from 'lucide-react';
import { brand } from '@shared/brand.js';
import { api, qs } from '../lib/api.js';
import { useSession } from './session.jsx';
import { useRealtime } from './realtime.js';
import { useOnline } from './hooks.js';
import { Menu } from '../components/Menu.jsx';
import { Avatar, IconButton } from '../components/ui.jsx';
import { CaptureSheet } from '../features/capture/CaptureSheet.jsx';

export const NAV = [
  {
    group: 'Dia a dia',
    items: [
      { to: '/', label: 'Início', icon: Home, end: true },
      { to: '/entrada', label: 'Caixa de entrada', icon: Inbox },
      { to: '/tarefas', label: 'Tarefas', icon: ClipboardList },
      { to: '/compras', label: 'Compras', icon: ShoppingCart },
      { to: '/despensa', label: 'Despensa', icon: Package },
      { to: '/refeicoes', label: 'Refeições', icon: UtensilsCrossed },
    ],
  },
  {
    group: 'Casa',
    items: [
      { to: '/financas', label: 'Finanças', icon: Wallet },
      { to: '/agenda', label: 'Agenda', icon: CalendarDays },
      { to: '/manutencao', label: 'Manutenção', icon: Wrench },
      { to: '/documentos', label: 'Documentos', icon: FileText },
    ],
  },
];

const CaptureContext = createContext(() => {});
/** Abre a captura rápida de qualquer tela: openCapture() ou openCapture({ type: 'task' }). */
export function useCapture() {
  return useContext(CaptureContext);
}

function HouseholdSwitcher() {
  const { households, household, householdId, switchHousehold, role } = useSession();
  const navigate = useNavigate();
  const roleLabel = { owner: 'Proprietário(a)', admin: 'Administrador(a)', member: 'Morador(a)' }[role] ?? '';
  return (
    <Menu
      label="Trocar de lar"
      align="left"
      trigger={(p) => (
        <button type="button" className="household-switch" {...p}>
          <span style={{ minWidth: 0 }}>
            <span className="hs-label">{roleLabel || 'Lar'}</span>
            <span className="hs-name truncate" style={{ display: 'block' }}>
              {household?.name ?? '…'}
            </span>
          </span>
          <ChevronDown aria-hidden="true" />
        </button>
      )}
      items={[
        { heading: 'Seus lares' },
        ...households.map((h) => ({
          label: h.name,
          icon: h.id === householdId ? Check : Home,
          current: h.id === householdId,
          onClick: async () => {
            if (h.id !== householdId) {
              await switchHousehold(h.id);
              navigate('/');
            }
          },
        })),
        { separator: true },
        { label: 'Configurações do lar', icon: Settings, onClick: () => navigate('/configuracoes/lar') },
        { label: 'Criar outro lar', icon: Plus, onClick: () => navigate('/boas-vindas?novo=1') },
      ]}
    />
  );
}

function AccountMenu() {
  const { user, logout, platformRoles, condominiums } = useSession();
  const navigate = useNavigate();
  return (
    <Menu
      label="Conta"
      trigger={(p) => (
        <button type="button" className="icon-btn" aria-label={`Conta de ${user?.name}`} {...p}>
          <Avatar name={user?.name} />
        </button>
      )}
      items={[
        { heading: user?.name },
        {
          label: 'Minha conta e preferências',
          icon: CircleUser,
          onClick: () => navigate('/configuracoes/conta'),
        },
        { label: 'Configurações do lar', icon: Settings, onClick: () => navigate('/configuracoes/lar') },
        ...(condominiums.length
          ? [{ label: 'Meu condomínio', icon: Building2, onClick: () => navigate('/condominio') }]
          : []),
        { label: 'Área do prestador', icon: Wrench, onClick: () => navigate('/prestador') },
        ...(platformRoles.length
          ? [{ label: 'Administração da plataforma', icon: ShieldCheck, onClick: () => navigate('/admin') }]
          : []),
        { separator: true },
        {
          label: 'Sair',
          icon: LogOut,
          danger: true,
          onClick: async () => {
            await logout();
            navigate('/entrar');
          },
        },
      ]}
    />
  );
}

/** Quantos itens chegaram e ainda esperam decisão. */
function useInboxCount() {
  const { householdId } = useSession();
  const q = useQuery({
    queryKey: [householdId, 'inbox', 'count'],
    queryFn: () => api(`/api/households/${householdId}/inbox/count`),
    enabled: Boolean(householdId),
  });
  return q.data?.pending ?? 0;
}

function useUnreadCount() {
  const { householdId } = useSession();
  const q = useQuery({
    queryKey: ['notifications', 'count', householdId],
    queryFn: () => api(`/api/notifications${qs({ householdId, limit: 1 })}`),
    enabled: Boolean(householdId),
  });
  return q.data?.unreadCount ?? 0;
}

export function AppShell() {
  const { householdId, user, condominiums } = useSession();
  const online = useOnline();
  const unread = useUnreadCount();
  const inboxPending = useInboxCount();
  const navigate = useNavigate();
  const [capture, setCapture] = useState(null);
  useRealtime(householdId, user?.id);

  const openCapture = (preset = {}) => setCapture(preset);

  return (
    <CaptureContext.Provider value={openCapture}>
      <a href="#conteudo" className="skip-link">
        Pular para o conteúdo
      </a>
      <div className="app">
        <aside className="sidebar" aria-label="Menu principal">
          <Link to="/" className="brand">
            <img src="/favicon.svg" alt="" />
            {brand.name}
          </Link>
          <HouseholdSwitcher />
          <button
            type="button"
            className="btn btn-primary btn-block capture-cta"
            onClick={() => openCapture()}
          >
            <Plus aria-hidden="true" /> Adicionar rápido
          </button>
          <nav>
            {NAV.map((g) => (
              <div key={g.group} style={{ display: 'contents' }}>
                <div className="nav-group">{g.group}</div>
                {g.items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.end}>
                    <it.icon aria-hidden="true" />
                    {it.label}
                    {it.to === '/entrada' && inboxPending > 0 && (
                      <span className="count">{inboxPending}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
          <nav className="sidebar-footer">
            {condominiums.length > 0 && (
              <NavLink to="/condominio">
                <Building2 aria-hidden="true" />
                Meu condomínio
              </NavLink>
            )}
            <NavLink to="/notificacoes">
              <Bell aria-hidden="true" />
              Notificações
              {unread > 0 && <span className="count">{unread}</span>}
            </NavLink>
            <NavLink to="/configuracoes/lar">
              <Settings aria-hidden="true" />
              Configurações
            </NavLink>
          </nav>
        </aside>

        <div style={{ minWidth: 0 }}>
          <header className="topbar">
            <div className="hide-desktop" style={{ minWidth: 0, flex: 1 }}>
              <HouseholdSwitcher />
            </div>
            <div className="spacer show-desktop" />
            <IconButton
              icon={Bell}
              label={unread ? `Notificações (${unread} não lidas)` : 'Notificações'}
              badge={unread}
              onClick={() => navigate('/notificacoes')}
            />
            <AccountMenu />
          </header>
          {!online && (
            <div className="offline-banner" role="status">
              <WifiOff aria-hidden="true" /> Você está sem conexão. Alterações só serão salvas quando a
              conexão voltar.
            </div>
          )}
          <main id="conteudo" className="main" tabIndex={-1}>
            <div className="main-inner">
              <Outlet />
            </div>
          </main>
        </div>

        <nav className="bottombar" aria-label="Navegação rápida">
          <NavLink to="/" end>
            <Home aria-hidden="true" />
            Início
          </NavLink>
          <NavLink to="/tarefas">
            <ClipboardList aria-hidden="true" />
            Tarefas
          </NavLink>
          <button
            type="button"
            className="capture-btn"
            onClick={() => openCapture()}
            aria-label="Adicionar rápido"
          >
            <span className="fab">
              <Plus aria-hidden="true" />
            </span>
          </button>
          <NavLink to="/compras">
            <ShoppingCart aria-hidden="true" />
            Compras
          </NavLink>
          <NavLink to="/mais">
            <LayoutGrid aria-hidden="true" />
            Mais
          </NavLink>
        </nav>
      </div>
      <CaptureSheet preset={capture} onClose={() => setCapture(null)} />
    </CaptureContext.Provider>
  );
}
